import { ConfigConstants } from './config';
import { Token } from './types';

export type AiStreamProgressEvent = {
  type: 'progress';
  stage?: string;
  message?: string;
};

export type AiStreamErrorEvent = {
  type: 'error';
  code?: string;
  message?: string;
};

export type AiStreamResultEvent = {
  type: 'result';
  [key: string]: unknown;
};

export type AiStreamEvent =
  | AiStreamProgressEvent
  | AiStreamErrorEvent
  | AiStreamResultEvent
  | { type: string; [key: string]: unknown };

export type ConsumeSseHandlers<TResult> = {
  onProgress?: (stage: string, message: string) => void;
  onResult?: (data: TResult) => void;
  onError?: (code: string | undefined, message: string) => void;
  onEvent?: (type: string, data: AiStreamEvent) => void;
};

const getAccessToken = (): string | undefined => {
  try {
    const token = JSON.parse(localStorage.getItem('token') || '{}') as Token;
    return token.accessToken;
  } catch {
    return undefined;
  }
};

const parseSseChunk = (
  chunk: string,
  onEvent: (eventName: string, data: string) => void
) => {
  const blocks = chunk.split('\n\n');
  for (const block of blocks) {
    if (!block.trim()) continue;

    let eventName = 'message';
    const dataLines: string[] = [];

    for (const rawLine of block.split('\n')) {
      const line = rawLine.replace(/\r$/, '');
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    }

    if (dataLines.length > 0) {
      onEvent(eventName, dataLines.join('\n'));
    }
  }
};

/**
 * Consumes a text/event-stream response using fetch (supports Authorization headers).
 */
export async function consumeSse<TResult = unknown>(
  path: string,
  init: RequestInit,
  handlers: ConsumeSseHandlers<TResult>
): Promise<void> {
  const baseUrl = ConfigConstants.baseUrl || '';
  const url = path.startsWith('http')
    ? path
    : `${baseUrl.replace(/\/?$/, '/')}${path.replace(/^\//, '')}`;

  const headers = new Headers(init.headers || {});
  if (!headers.has('Accept')) {
    headers.set('Accept', 'text/event-stream');
  }

  const accessToken = getAccessToken();
  if (accessToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const response = await fetch(url, {
    ...init,
    headers
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body?.message || body?.Message || message;
    } catch {
      // ignore parse errors
    }
    handlers.onError?.(String(response.status), message);
    return;
  }

  if (!response.body) {
    handlers.onError?.(undefined, 'No response body received from stream');
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let sawTerminalEvent = false;

  const handleEvent = (_eventName: string, data: string) => {
    let parsed: AiStreamEvent;
    try {
      parsed = JSON.parse(data) as AiStreamEvent;
    } catch {
      return;
    }

    const type = parsed.type || _eventName;
    handlers.onEvent?.(type, parsed);

    if (type === 'progress') {
      handlers.onProgress?.(
        (parsed as AiStreamProgressEvent).stage || '',
        (parsed as AiStreamProgressEvent).message || ''
      );
      return;
    }

    if (type === 'error') {
      sawTerminalEvent = true;
      handlers.onError?.(
        (parsed as AiStreamErrorEvent).code,
        (parsed as AiStreamErrorEvent).message || 'An error occurred'
      );
      return;
    }

    if (type === 'result') {
      sawTerminalEvent = true;
      handlers.onResult?.(parsed as TResult);
    }
  };

  try {
    let streamDone = false;
    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) {
        streamDone = true;
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      const lastSeparator = buffer.lastIndexOf('\n\n');
      if (lastSeparator === -1) continue;

      const complete = buffer.slice(0, lastSeparator + 2);
      buffer = buffer.slice(lastSeparator + 2);
      parseSseChunk(complete, handleEvent);
    }

    if (buffer.trim()) {
      parseSseChunk(buffer, handleEvent);
    }

    if (!sawTerminalEvent) {
      handlers.onError?.(undefined, 'Stream ended without a result');
    }
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') {
      return;
    }
    throw err;
  }
}
